/**
 * Subscribe to a CSS media query from React. Backed by `useSyncExternalStore`
 * so the first render already reads the real match (no `useEffect` flash where
 * the desktop layout paints for a frame before snapping to mobile). Concurrent
 * safe and cheap — `matchMedia` results are memoized by the browser.
 */
import { useSyncExternalStore } from 'react'

export const useMediaQuery = (query: string): boolean =>
    useSyncExternalStore(
        (onChange) => {
            const mql = window.matchMedia(query)
            mql.addEventListener('change', onChange)
            return () => mql.removeEventListener('change', onChange)
        },
        () => window.matchMedia(query).matches,
        // Server snapshot — this is a client-only SPA, so "not mobile" is the
        // safe default and is corrected on the first client read anyway.
        () => false,
    )

/**
 * True below Tailwind's `md` breakpoint (768px): phones in portrait, and most
 * phones in landscape. The dashboard swaps its drag/resize grid for a single
 * column stack at this width — react-grid-layout keeps its desktop column count
 * at every viewport, so without the switch a phone renders twelve ~27px columns.
 */
export const useIsMobile = (): boolean => useMediaQuery('(max-width: 767px)')
